<script lang="ts">
  import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
  import { IOrderEmailSettings } from '../../../interfaces/IOrderEmailSettings';
  import { OrderStatus } from '../../../enums/OrderEnums';
  import 'quill/dist/quill.core.css';
  import 'quill/dist/quill.snow.css';
  import 'quill/dist/quill.bubble.css';

  import { quillEditor } from 'vue-quill-editor';
  import { vueData } from '../../../VueData';
  import StaticService from '../../../api/services/static.service';
  import { Globals } from '../../../modules/Globals';
  import { AlertType } from '../../../interfaces/IAlert';

  const toolbarOptions = [
    ['bold', 'italic', 'underline', 'strike'], // toggled buttons
    ['blockquote', 'code-block'],
    [{ header: 1 }, { header: 2 }], // custom button values
    [{ list: 'ordered' }, { list: 'bullet' }],
    [{ script: 'sub' }, { script: 'super' }], // superscript/subscript
    [{ indent: '-1' }, { indent: '+1' }], // outdent/indent
    [{ direction: 'rtl' }], // text direction
    [{ size: ['small', false, 'large', 'huge'] }], // custom dropdown
    [{ header: [1, 2, 3, 4, 5, 6, false] }],
    [{ color: [] }, { background: [] }], // dropdown with defaults from theme
    [{ font: [] }],
    [{ align: [] }],
    ['clean'],
    ['link', 'image', 'video'], // remove formatting button
  ];

  @Component({ components: { quillEditor } })
  export default class OrderEmailSettingsBasic extends Vue {
    $refs: {
      quill: quillEditor;
      imageInput: HTMLInputElement;
    };
    @Prop() settings: IOrderEmailSettings;
    @Prop() value: boolean;
    staticService = new StaticService();
    emailConditions = [];
    valid = false;
    wantsHtmlTabIndex = 0;
    emailWhenOptions = [
      { value: 0, title: 'Inmediatamente' },
      { value: 15, title: '15 minutos' },
      { value: 30, title: 'Media hora' },
      { value: 60, title: 'Una hora' },
      { value: 120, title: 'Dos horas' },
      { value: 360, title: 'Seis horas' },
      { value: 720, title: 'Doce horas' },
      { value: 60 * 24, title: 'Un día' },
    ];
    /*editorOptions = {
      modules: {
        toolbar: {
          container: toolbarOptions, // toolbar
          handlers: {
            image: this.onImageToolClick,
          },
        },
      },
    };*/

    async created() {
      this.emailConditions = [
        OrderStatus.Received,
        OrderStatus.Progress,
        OrderStatus.Finished,
      ].map((o) => ({ value: o, title: this.$tc(`order.status.${o}`) }));

      await this.$nextTick();
    }

    async onEditorChange() {
      const imgs = Array.from(
        this.editor.quill.container.querySelectorAll(
          'img[src^="data:"]:not(.loading)'
        )
      );
      const oldValid = this.valid;
      this.valid = false;
      for (const img of imgs) {
        img.classList.add('loading');
        try {
          const data = await this.staticService.uploadImage({
            b64: img.getAttribute('src'),
            final: true,
          });
          // TODO instead of upload final, upload temp and move after saving
          img.setAttribute('src', Globals.uploadsPath + '/' + data.path);
        } catch (e) {
          vueData.addAlert(
            'Ocurrió un error, actualice la página',
            AlertType.Error
          );
          console.error(e);
        } finally {
          img.classList.remove('loading');
        }
      }
      this.valid = oldValid;
    }

    @Watch('settings.basicHtml', { immediate: true })
    onBasicChanged() {
      this.wantsHtmlTabIndex = this.settings.basicHtml ? 0 : 1;
    }

    @Watch('valid', { immediate: true })
    onValidChange(newVal: boolean) {
      this.$emit('input', newVal);
    }

    onImageToolClick(value) {
      if (value) {
        this.imageInput.click(); //call select image
      } else {
        this.editor.format('image', false);
      }
    }

    get editor() {
      return this.$refs.quill;
    }

    get imageInput() {
      return this.$refs.imageInput;
    }

    async onTabChange() {
      await this.$nextTick();
      let cancel = false;

      if (this.wantsHtmlTabIndex === 0 && !this.settings.basicHtml) {
        cancel = !(await vueData.showConfirmDialog(
          this.$modal,
          'Atención',
          'Volver al Editor básico modificará cualquier HTML avanzado que haya ingresado.'
        ));
      }

      if (cancel)
        this.$nextTick(
          () => (this.wantsHtmlTabIndex = this.settings.basicHtml ? 0 : 1)
        );
      else this.settings.basicHtml = !this.wantsHtmlTabIndex;
    }

    async doImageUpload(event) {
      // for simplicity I only upload the first image
      const file = event.target.files[0];
      // create form data
      const fd = new FormData();
      // just add file instance to form data normally
      fd.append('image', file);
      // I use axios here, should be obvious enough
      const response = await this.$axios.post(this.imageUpload.url, fd);
      // clear input value to make selecting the same image work
      event.target.value = '';
      // get current index of the cursor
      const currentIndex = this.editor.selection.lastRange.index;
      // insert uploaded image url to 'image' embed (quill does this for you)
      // the embed looks like this: <img src="{url}" />
      this.editor.insertEmbed(currentIndex, 'image', response.data.url);
      // set cursor position to after the image
      this.editor.setSelection(currentIndex + 1, 0);
    }
  }
</script>

<template>
  <div>
    <v-row>
      <v-col cols="12" sm="6">
        <v-select
          label="Enviar cuando el pedido está..."
          outlined
          dense
          :items="emailConditions"
          item-text="title"
          item-value="value"
          v-model="settings.condition"
          hide-details
        />
      </v-col>
      <v-col cols="12" sm="6">
        <v-select
          label="Dejar pasar..."
          outlined
          dense
          :items="emailWhenOptions"
          item-text="title"
          item-value="value"
          v-model="settings.when"
          hide-details
        />
      </v-col>
    </v-row>
    <hr class="my-4" />
    <v-radio-group
      label="¿Qué enviar?"
      v-model="settings.custom"
      hide-details
      dense
    >
      <v-radio label="Encuesta QueResto" :value="false" disabled />
      <v-radio label="Email personalizado" :value="true" />
    </v-radio-group>
    <hr class="my-4" />
    <v-form @submit.prevent v-model="valid">
      <v-row>
        <v-col cols="12" sm="6">
          <v-text-field
            label="Asunto del mail"
            outlined
            dense
            v-model="settings.subject"
            :rules="$rule('required')"
          />
        </v-col>
        <v-col cols="12" sm="6">
          <v-text-field
            label="Nombre de quién envía"
            placeholder="Usualmente el nombre de su empresa"
            outlined
            dense
            v-model="settings.fromName"
            :rules="$rule('required')"
          />
        </v-col>
      </v-row>
    </v-form @submit.prevent>
    <template v-if="settings.custom">
      <v-tabs v-model="wantsHtmlTabIndex" @change="onTabChange">
        <v-tab>Editor básico</v-tab>
        <v-tab>HTML</v-tab>
      </v-tabs>
      <div class="mt-4">
        <!--<input
          ref="imageInput"
          class="d-none"
          type="file"
          accept="image/*"
          @change="doImageUpload"
        />-->
        <quill-editor
          v-if="settings.basicHtml"
          ref="quill"
          v-model="settings.html"
          @change="onEditorChange"
        />
        <v-textarea
          v-else
          v-model="settings.html"
          outlined
          placeholder="Escriba o pegue el código HTML"
        />
      </div>
    </template>
  </div>
</template>

<style scoped lang="scss"></style>
